/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import dayjs from 'dayjs';

import { OSPatchInstall } from '@totem/components/devices/os-patch-installs/types';
import { PatchInstallsInput } from '@totem/components/osPatchReport/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { DeviceQueryResult } from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import { formatDateTimeDisplay } from '@totem/utilities/timeUtilities';

type Props = {
  patchInstalls: OSPatchInstall[];
};

const OsPatchesTable = ({ patchInstalls }: Props) => {
  const [severityFilterOptions, setSeverityFilterOptions] = useState<string[]>(
    [],
  );
  const [typeFilterOptions, setTypeFilterOptions] = useState<string[]>([]);
  const [filteredData, setFilteredData] =
    useState<OSPatchInstall[]>(patchInstalls);
  const [input, updateInput] = useState<PatchInstallsInput>({});

  // Set Filtered Data
  useEffect(() => {
    let tmpData = patchInstalls;

    if (isNotNull(input) && isNotNull(input.type) && input.type.length > 0) {
      tmpData = tmpData.filter((chk) => input.type.includes(chk.type));
    }
    if (
      isNotNull(input) &&
      isNotNull(input.severity) &&
      input.severity.length > 0
    ) {
      tmpData = tmpData.filter((chk) => input.severity.includes(chk.severity));
    }

    setFilteredData(tmpData);
  }, [patchInstalls, input]);

  // Set Filter Options
  useEffect(() => {
    const severities = new Set<string>();
    const types = new Set<string>();

    for (const patch of patchInstalls) {
      severities.add(patch.severity);

      types.add(patch.type);
    }

    const severityOptions = Array.from(severities);
    const typeOptions = Array.from(types);

    setSeverityFilterOptions(severityOptions);

    setTypeFilterOptions(typeOptions);
  }, [patchInstalls]);

  const setInput = (updated: Partial<PatchInstallsInput>) => {
    updateInput((prevInput) => {
      const newInput = { ...prevInput, ...updated };
      return newInput;
    });
  };

  const columns: ColumnProps<OSPatchInstall>[] = [
    {
      title: 'Patch Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (compA, compB) =>
        compA.name === compB.name ? 0 : compA.name > compB.name ? 1 : -1,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (compA, compB) =>
        compA.type === compB.type ? 0 : compA.type > compB.type ? 1 : -1,
      filterMultiple: true,
      filteredValue: stringifyArray(input.type),
      filters: typeFilterOptions.map((chk) => {
        return { text: chk, value: chk };
      }),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: (compA, compB) =>
        compA.severity === compB.severity
          ? 0
          : compA.severity > compB.severity
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: stringifyArray(input.severity),
      filters: severityFilterOptions.map((chk) => {
        return { text: chk, value: chk };
      }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (compA, compB) =>
        compA.status === compB.status
          ? 0
          : compA.status > compB.status
            ? 1
            : -1,
    },
    {
      title: 'KB Number',
      dataIndex: 'kbNumber',
      key: 'kbNumber',
      sorter: (compA, compB) =>
        compA.kbNumber === compB.kbNumber
          ? 0
          : compA.kbNumber > compB.kbNumber
            ? 1
            : -1,
    },
    {
      title: 'Installed At',
      dataIndex: 'installedAt',
      key: 'installedAt',
      defaultSortOrder: 'descend',
      render: (_, record: OSPatchInstall) =>
        formatDateTimeDisplay(dayjs.unix(record.installedAt).toISOString()),
      sorter: (compA, compB) =>
        compA.installedAt === compB.installedAt
          ? 0
          : compA.installedAt > compB.installedAt
            ? 1
            : -1,
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<DeviceQueryResult>,
    sorter,
  ) => {
    const { ...params } = filters;

    // @ts-ignore
    setInput({
      ...input,
      ...params,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(filteredData) ? filteredData : []}
      onChange={handleTableChange}
      loading={false}
      pagination={{
        showSizeChanger: true,
        defaultPageSize: 100,
      }}
      rowKey={(record) => record.id}
    />
  );
};

export default OsPatchesTable;
